import { CommandItem } from '@/components/ui/command';
import { cn } from '@/lib/cn';
import type {
  AnnotatingTagUsageNested,
  ObjectsRealEstate,
  ObjectsSegment,
  OrganizationCompany,
  OrganizationExternalClient,
} from '@pigello/construct-core';
import { AnimatePresence, motion } from 'framer-motion';
import { type FunctionComponent, type PropsWithChildren } from 'react';
import { Skeleton } from '@/components/ui/skeleton';

const FilterTag = ({ tag }: { tag: AnnotatingTagUsageNested }) => {
  if (!tag?.tag) {
    return null;
  }

  return (
    <div className='flex shrink-0 items-center gap-1.5 rounded-full border border-foreground/20 px-2 py-1 align-middle text-xs text-foreground/70'>
      <div
        style={{
          backgroundColor: tag?.tag?.colorCode?.startsWith('#')
            ? tag.tag.colorCode
            : `#${tag?.tag?.colorCode}`,
        }}
        className='size-2.5 rounded-full'
      />
      <span>{tag.tag?.name}</span>
    </div>
  );
};

type Item =
  | ObjectsSegment
  | ObjectsRealEstate
  | OrganizationCompany
  | OrganizationExternalClient;

interface Props<TItem extends Item> extends Omit<
  React.ComponentProps<typeof CommandItem>,
  'onToggle' | 'onSelect' | 'value'
> {
  item: TItem;
  onToggle: (id: string, name: string) => void;
  isSelected: boolean;
}

// Rendered as a cmdk CommandItem so the palette's input can arrow-navigate
// the rows and toggle with Enter. Consumers must mount the rows inside a
// `Command` (the palette shell provides one; the legacy modal wraps its own).
function GlobalFilterListItem<TItem extends Item>({
  item,
  children,
  onToggle,
  isSelected,
  ref,
  disabled,
  ...props
}: Props<TItem>) {
  return (
    <CommandItem
      ref={ref}
      {...props}
      value={item.id}
      disabled={disabled}
      className={cn(
        'group flex w-full items-center justify-between rounded-none border-b bg-surface px-4 py-3 text-start transition-all duration-300 hover:bg-accent',
        {
          'bg-primary/5 hover:bg-primary/10': isSelected,
          'opacity-50': disabled,
        }
      )}
      aria-pressed={isSelected}
      onSelect={() => onToggle(item.id, item.name ?? '')}
    >
      <div className='shrink-0'>
        <div className='flex gap-2'>
          {item.customId && (
            <p className='text-sm text-foreground/50'>{item.customId}</p>
          )}
          <p className='text-sm'>{item.name}</p>
        </div>
        {children}
      </div>
      <div className='flex flex-1 flex-wrap justify-end gap-2 pr-2'>
        {item.tags &&
          item.tags.map((tag) => <FilterTag tag={tag} key={tag.id} />)}
      </div>
      <div className='flex items-center gap-2'>
        <div
          className={cn(
            'size-5 rounded-md border border-foreground/50 p-0.5 transition-all group-active:scale-[80%]',
            {
              'border-primary': isSelected,
            }
          )}
        >
          <AnimatePresence>
            {isSelected && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{
                  scale: 1,
                  opacity: 1,
                  transition: { type: 'tween' },
                }}
                exit={{ scale: [1, 1.2, 0], opacity: 0 }}
                className='size-full rounded-sm bg-primary'
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    </CommandItem>
  );
}

const GlobalFilerListItemSkeleton: FunctionComponent<PropsWithChildren> = ({
  children,
}) => (
  <div className='flex items-center justify-between border-b bg-surface px-4 py-3'>
    <div className='flex w-full flex-col gap-1.5'>
      <Skeleton className='h-3.5 w-48' />
      {children}
    </div>
    <div className='flex items-center gap-2'>
      <Skeleton className='h-6.5 w-32 rounded-full' />
      <Skeleton className='size-5 rounded-md' />
    </div>
  </div>
);

export { GlobalFilerListItemSkeleton, GlobalFilterListItem };
